import React, {useCallback, useEffect, useState} from "react";

import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {Box, Flex, Icon, IconButton, Image, Stack} from "@chakra-ui/react";
import {Folder, LogOut, Menu, NotebookPen, X, CalendarFold, Newspaper, MapPin, Users, Handshake, LineChart, BookOpen, List} from 'lucide-react';

import {useUser} from "@/contexts/userContext";
import {UserPreview, Button} from '@/components';
import logo from '@/assets/images/logo-cartouche-secondary.png'
import {useLanguage} from "@/contexts/languageContext";

export const App = () => {
    const { t } = useLanguage();
    const {logout, user} = useUser();
    const location = useLocation();
    const navigate = useNavigate();

    const isSelectedRoot = (...path) => {
        const splitted = location.pathname.split('/');
        return path.findIndex(e => location?.pathname &&
            (
                (splitted.length > 2 && location.pathname.startsWith(e)) ||
                (splitted.length === 2 && `/${splitted[1]}` === e)
            )) !== -1
    };

    const isSelected = (...path) => {
        return path.findIndex(e => e === location.pathname) !== -1
    };

    const selectedProps = useCallback((root, ...path) => {
        const fn = root === true ? isSelectedRoot : isSelected;
        return fn(...path)
            ? {}
            : {bg: "transparent"};
    }, [location?.pathname]);

    const [isMenuOpen, setIsMenuOpen] = useState(location?.pathname === '/');

    useEffect(() => {
        setIsMenuOpen(_ => location?.pathname === '/')
    },[location]);

    const toggleMenu = () => {
        setIsMenuOpen(prevState => !prevState);
    };

    return (
        <Box style={{display: 'flex', alignItems: 'stretch', height: '100vh', overflow: {base:'auto', lg:'hidden'} , maxWidth: '100vw', minWidth: '100vw' }} bg="secondary.400">
            <IconButton
                aria-label="Toggle menu"
                icon={isMenuOpen ? <X color="secondary.500"/> : <Menu color="secondary.500"/>}
                onClick={toggleMenu}
                display={{ base: 'block', lg: 'none' }}
                position="fixed"
                top="1rem"
                left="1rem"
                zIndex={4}
                isRound={true}
                variant="iconOnly"
                boxSize={10}
            />
            <Box
                display={{ base: isMenuOpen ? 'flex' : 'none', lg: 'flex' }}
                flexDirection="column"
                borderRadius={{base: '0', lg: '0 15px 15px 0'}}
                bg="base.500"
                zIndex={3}
                position={{ base: 'fixed', lg: 'static' }}
                width={{ base: '100%', lg: 'auto' }}
                maxH="100vh"
                minH="100vh"
                p="1.5vw"
                overflow="hidden"
                style={{ gap: "2rem" }}
            >
                <Box minH="10vh" maxH="10vh" maxW="20vw" display="flex" justifyContent="center">
                    <Image maxW="70%" maxH="100%" w="auto" h="auto" src={logo} alt="Logo"/>
                </Box>
                <Stack direction="column" gap="2.5rem" flex={1} px="0" py="1rem" overflow="hidden">
                    {user.role === "ADMIN" &&
                        <Stack className="mainMenu" direction="column" flex={1} overflowY="overlay" px=".25rem" w="100%">
                            <Button mb=".5rem" onClick={() => navigate('/poll')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(false, '/poll')}><Icon as={NotebookPen} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.poll_other') })}</Button>
                            <Button mb=".5rem" onClick={() => navigate('/article')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(true, '/article')}><Icon as={Newspaper} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.article_other') })}</Button>
                            {
                                isSelectedRoot('/article') &&
                                <Stack direction='column' gap={0} px="2vw">
                                    <Button onClick={() => navigate('/article')} mb=".5rem" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/article')}>{t('wrappers.capitalize', { value: t('objects.article_other') })}</Button>
                                    <Button mb=".5rem" onClick={() => navigate('/article/tag')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/article/tag')}>{t('wrappers.capitalize', { value: t('objects.tag_other') })}</Button>
                                </Stack>
                            }
                            <Button mb=".5rem" onClick={() => navigate('/resource')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(true, '/resource')}><Icon as={Folder} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.resource_other') })}</Button>
                            {
                                isSelectedRoot('/resource') &&
                                <Stack direction='column' gap={0} px="2vw">
                                    <Button onClick={() => navigate('/resource')} mb=".5rem" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/resource')}>{t('wrappers.capitalize', { value: t('objects.resource_other') })}</Button>
                                    <Button onClick={() => navigate('/resource/category')} mb=".5rem" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/resource/category')}>{t('wrappers.capitalize', { value: t('objects.category_other') })}</Button>
                                </Stack>
                            }
                            <Button mb=".5rem" onClick={() => navigate('/event')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(false, '/event')}><Icon as={CalendarFold} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.event_other') })}</Button>
                            <Button mb=".5rem" onClick={() => navigate('/poi')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(true, '/poi')}><Icon as={MapPin} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.poi_other') })}</Button>
                            {
                                isSelectedRoot('/poi') &&
                                <Stack direction='column' gap={0} px="2vw">
                                    <Button onClick={() => navigate('/poi')} mb=".5rem" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/poi')}>{t('wrappers.capitalize', { value: t('objects.poi_other') })}</Button>
                                    <Button onClick={() => navigate('/poi/category')} mb=".5rem" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/poi/category')}>{t('wrappers.capitalize', { value: t('objects.category_other') })}</Button>
                                </Stack>
                            }
                            <Button mb=".5rem" onClick={() => navigate('/user')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(false, '/user')}><Icon as={Users} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.user_other') })}</Button>
                            <Button mb=".5rem" onClick={() => navigate('/partner')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(false, '/partner')}><Icon as={Handshake} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.partner_other') })}</Button>
                            <Button mb=".5rem" onClick={() => navigate('/guide')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(true, '/guide')}><Icon as={BookOpen} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.guide_other') })}</Button>
                            {
                                isSelectedRoot('/guide') &&
                                <Stack direction='column' gap={0} px="2vw">
                                    <Button onClick={() => navigate('/guide')} mb=".5rem" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/guide')}>{t('wrappers.capitalize', { value: t('objects.guide_other') })}</Button>
                                    <Button onClick={() => navigate('/guide/type')} mb=".5rem" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/guide/type')}>{t('wrappers.capitalize', { value: t('objects.guide_type_other') })}</Button>
                                </Stack>
                            }
                            <Button mb=".5rem" onClick={() => navigate('/list/club')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(true, '/list')}><Icon as={List} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.list_other') })}</Button>
                            {
                                isSelectedRoot('/list') &&
                                <Stack direction='column' gap={0} px="2vw">
                                    <Button onClick={() => navigate('/list/club')} mb=".5rem" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/list/club')}>{t('wrappers.capitalize', { value: t('objects.club_other') })}</Button>
                                    <Button onClick={() => navigate('/list/division')} mb=".5rem" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" pl=".5rem" size="sm" {...selectedProps(false, '/list/division')}>{t('wrappers.capitalize', { value: t('objects.division_other') })}</Button>
                                </Stack>
                            }
                            <Button mb=".5rem" onClick={() => navigate('/stats')} borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" {...selectedProps(false, '/stats')}><Icon as={LineChart} mr=".5rem"/>{t('wrappers.capitalize', { value: t('objects.stats') })}</Button>
                        </Stack>
                    }
                    <UserPreview/>
                    <Button variant="iconOnly" mt="auto" borderRadius="1rem" style={{ justifyContent:'flex-start'}} h="auto" onClick={logout}><Icon as={LogOut} mr=".5rem"/>{t('misc.logout')}</Button>
                </Stack>
            </Box>
            <Flex flex={1} bg="primary.500" overflow={{ base: 'auto', lg: 'hidden'}}>
                <Outlet/>
            </Flex>
        </Box>
    );
};

export default App;
